/**
 * Forecast Settings Tab — tenant-wide toggles for the Purchasing > Forecast
 * schedule grid: Total row, weekly horizon, PO Number and Supplier Part No
 * columns and the part description source. The grid switches are read on the
 * customer whose demand is shown, so suppliers see the same grid.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { LineChart, Loader2 } from 'lucide-react';
import { BarrattFlags } from './types';

interface ForecastSettingsTabProps {
  barrattFlags: BarrattFlags;
  saving: boolean;
  onBarrattFlagsChange: (flags: BarrattFlags) => void;
  onSave: () => void;
}

export function ForecastSettingsTab({
  barrattFlags,
  saving,
  onBarrattFlagsChange,
  onSave,
}: ForecastSettingsTabProps) {
  const update = (updates: Partial<BarrattFlags>) => {
    onBarrattFlagsChange({ ...barrattFlags, ...updates });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LineChart className="h-5 w-5" />
            Forecast Settings
          </CardTitle>
          <CardDescription>
            Controls for the Purchasing &rarr; Forecast schedule grid.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between gap-4 p-3 rounded-md border">
            <div>
              <Label className="font-medium">Show Total row</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Render the purple Total row beneath every Open PO + Forecast
                pair. The Total row is the sum of Open PO and Forecast
                quantities per bucket, as in the export. On by default; turn
                off to keep the grid compact when the combined number isn't
                needed. Applies to customer and supplier users.
              </p>
            </div>
            <Switch
              checked={barrattFlags.show_forecast_total}
              onCheckedChange={(checked) => update({ show_forecast_total: checked })}
            />
          </div>

          <div className="flex items-start justify-between gap-4 p-3 rounded-md border">
            <div>
              <Label className="font-medium">Weekly horizon only (25 weeks)</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Show only the weekly buckets on the Forecast grid and its
                export. When off (default) the grid shows the full two-year
                horizon: weeks, then monthly buckets, then Future.
              </p>
            </div>
            <Switch
              checked={barrattFlags.forecast_weeks_only}
              onCheckedChange={(checked) => update({ forecast_weeks_only: checked })}
            />
          </div>
          {/* Customer review 11 Sep (item 35a) */}
          <div className="flex items-start justify-between gap-4 p-3 rounded-md border">
            <div>
              <Label className="font-medium">Show PO Number</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Show the open purchase order number(s) behind each part as a
                column on the Forecast grid and its export. On by default only
                where forecast rows are purchase order schedules beyond the
                firm window (part scheduling). Elsewhere a forecast is demand
                not yet raised to an order, so a Forecast row never shows a PO
                number, whatever this switch says. Applies to customer and
                supplier users.
              </p>
            </div>
            <Switch
              checked={barrattFlags.forecast_show_po_number}
              onCheckedChange={(checked) => update({ forecast_show_po_number: checked })}
            />
          </div>
          <div className="flex items-start justify-between gap-4 p-3 rounded-md border">
            <div>
              <Label className="font-medium">Show Supplier Part No</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Show the supplier's own part reference as a column on the
                Forecast grid and its export. On by default; turn off where
                suppliers are not given part references, so the grid does not
                carry a column of dashes. Applies to customer and supplier users.
              </p>
            </div>
            <Switch
              checked={!barrattFlags.forecast_hide_supplier_part_no}
              onCheckedChange={(checked) => update({ forecast_hide_supplier_part_no: !checked })}
            />
          </div>
          <div className="flex items-start justify-between gap-4 p-3 rounded-md border">
            <div>
              <Label className="font-medium">Part description from the part master</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Show each part&apos;s description from the part master (Parts
                Master, then Part Catalogue) instead of the text on the forecast
                or purchase order line. The purchase order line&apos;s own text
                (its order requirements) then moves to an Order Requirements
                column on the grid and export rather than being lost. Off by
                default: a part with no description is filled from the master
                either way.
              </p>
            </div>
            <Switch
              checked={barrattFlags.forecast_description_from_master}
              onCheckedChange={(checked) => update({ forecast_description_from_master: checked })}
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={onSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
